import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { Bucket, BucketHeader, Checkbox, SearchInput } from "~/core";
import { messages } from "../i18n-messages";
import { IReportType, ISelectReportType } from "../data/interfaces";

export interface IReportTypesProps {
    intl: injectIntl;
    reportTypes?: IReportType[];
    selectReportType?: (report: ISelectReportType) => void;
    selectedReportTypes?: string[];
    withRecentUsed?: boolean;
    onSearchChange?: (value?: string) => void;
}

const ReportTypes = ({
    intl,
    reportTypes,
    selectReportType,
    selectedReportTypes,
    withRecentUsed,
}: IReportTypesProps): JSX.Element => {
    const [searchValue, setSearchValue] = useState<string>(null);

    const isReportTypeSelected = ({ reportTypeGuid }) => {
        return selectedReportTypes.indexOf(reportTypeGuid) > -1;
    };

    const getLabelString = () => {
        return `1. ${intl.formatMessage(messages.selectReports)}:`;
    };

    const recentTypes = useMemo(
        () =>
            withRecentUsed
                ? reportTypes
                      ?.filter((x) => Boolean(x.lastRun))
                      .sort(
                          (a, b) =>
                              b.lastRun.localeCompare(a.lastRun) ||
                              a.reportTypeDisplayName.localeCompare(b.reportTypeDisplayName)
                      )
                      .slice(0, 10)
                      .sort((a, b) =>
                          a.reportTypeDisplayName.localeCompare(b.reportTypeDisplayName)
                      ) ?? []
                : [],
        [reportTypes, withRecentUsed]
    );

    const filteredReportTypes = useMemo(
        () =>
            !searchValue
                ? reportTypes
                : reportTypes.filter((x) =>
                      x.reportTypeDisplayName.toLocaleLowerCase().includes(searchValue)
                  ),
        [searchValue, reportTypes]
    );

    const outputCheckbox = (reportType: IReportType, index: number) => (
        <Checkbox
            key={index}
            value={isReportTypeSelected(reportType)}
            label={reportType.reportTypeDisplayName}
            onChange={() =>
                selectReportType({
                    name: reportType.reportTypeName,
                    guid: reportType.reportTypeGuid,
                })
            }
            testId={`report-types-report-type-checkbox-${index}`}
        />
    );

    const outputRecentLayout = () =>
        reportTypes.length > 0 && (
            <div className="type-buckets">
                {recentTypes.length > 0 && !searchValue && (
                    <Bucket
                        isExpanded
                        showSymbol={true}
                        isCollapsible={true}
                        testId="report-types-recent-bucket"
                    >
                        <BucketHeader>{intl.formatMessage(messages.recent)}</BucketHeader>
                        {recentTypes.map((reportType, index) => outputCheckbox(reportType, index))}
                    </Bucket>
                )}
                <Bucket
                    isExpanded
                    showSymbol={true}
                    isCollapsible={true}
                    testId="report-types-all-bucket"
                >
                    <BucketHeader>{intl.formatMessage(messages.all)}</BucketHeader>
                    {filteredReportTypes?.map((reportType, index) =>
                        outputCheckbox(reportType, index + recentTypes.length)
                    )}
                </Bucket>
            </div>
        );

    return !withRecentUsed ? (
        <div className="report-types">
            <div className="options-header">{getLabelString()}</div>
            {withRecentUsed
                ? outputRecentLayout()
                : reportTypes?.map((reportType, index) => outputCheckbox(reportType, index))}
        </div>
    ) : (
        <div className="report-types">
            <div className="with-recent">
                <div className="options-header">
                    <div>{getLabelString()}</div>
                    <SearchInput
                        onSearchChange={(value) => setSearchValue(value?.toLocaleLowerCase())}
                    />
                </div>
                {outputRecentLayout()}
            </div>
        </div>
    );
};

ReportTypes.propTypes = {
    intl: intlShape,
    reportTypes: PropTypes.array,
    selectReportType: PropTypes.func,
    selectedReportTypes: PropTypes.array,
    withRecentUsed: PropTypes.bool,
    onSearchChange: PropTypes.func,
};

export default injectIntl(ReportTypes);
